import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Box,
  Users,
  BarChart3,
  Settings,
  TrendingUp,
  Smile,
  Megaphone,
  Layers,
  Search,
  Target,
  FileText,
  Store,
  Briefcase,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const CAPABILITIES = [
  {
    title: "Product Reach",
    desc: "Drive awareness and adoption of your products across channels.",
    icon: Box,
    iconColor: "#2563EB",
    iconBg: "#EBF3FF",
  },
  {
    title: "Customer Loyalty",
    desc: "Reward purchases and build long-term customer relationships.",
    icon: Users,
    iconColor: "#EA580C",
    iconBg: "#FEF3EB",
  },
  {
    title: "Performance Tracking",
    desc: "Track campaign and sales performance in real time.",
    icon: BarChart3,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
  },
  {
    title: "Easy Management",
    desc: "Configure and manage programs with minimal effort.",
    icon: Settings,
    iconColor: "#16A34A",
    iconBg: "#ECFDF5",
  },
  {
    title: "Real-Time Insights",
    desc: "Get actionable insights across customers, campaigns and regions.",
    icon: TrendingUp,
    iconColor: "#16A34A",
    iconBg: "#ECFCCB",
  },
  {
    title: "Engagement Growth",
    desc: "Run interactive promotions and gamified campaigns.",
    icon: Smile,
    iconColor: "#E11D48",
    iconBg: "#FFE4E6",
  },
  {
    title: "Marketing Optomization",
    desc: "Optimize offers and incentives based on data-driven insights.",
    icon: Megaphone,
    iconColor: "#4F46E5",
    iconBg: "#EEF2FF",
  },
  {
    title: "Operational Efficiency",
    desc: "Automate workflows and reduce manual effort.",
    icon: Layers,
    iconColor: "#CA8A04",
    iconBg: "#FEF9C3",
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    number: "01",
    title: "Set up your Platform",
    desc: "Configure your brand, products, users and program rules.",
    icon: Search,
    iconColor: "#2563EB",
    iconBg: "#EBF3FF",
  },
  {
    number: "02",
    title: "Customize your Program",
    desc: "Create rewards, promotions and campaigns tailored to your business goals.",
    icon: Box,
    iconColor: "#9333EA",
    iconBg: "#F5EEFD",
  },
  {
    number: "03",
    title: "Launch & Grow",
    desc: "Go live, engage your customers and track performance in real time.",
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "#EBFDF2",
  },
];

const ANALYTICS_FEATURES = [
  {
    title: "Campaign Performance",
    desc: "Track redemptions, engagement and ROI across all campaigns.",
    icon: BarChart3,
    iconColor: "#0891B2",
    iconBg: "#DDF6FA",
  },
  {
    title: "Customer Insights",
    desc: "Understand buying behavior and loyalty trends.",
    icon: Users,
    iconColor: "#E11D48",
    iconBg: "#FEEAEA",
  },
  {
    title: "Sales Team Effectiveness",
    desc: "Measure team performance and incentive impact.",
    icon: Target,
    iconColor: "#65A30D",
    iconBg: "#EDFAC8",
  },
  {
    title: "Flexible Reporting",
    desc: "Custom dashboards and reports for all stakeholders.",
    icon: FileText,
    iconColor: "#DB2777",
    iconBg: "#FCE7F3",
  },
];

const STAKEHOLDERS = [
  {
    title: "Channel Partners",
    desc: "Strengthen partner loyalty with targeted incentives.",
    image: "/images/products/channel-partners.jpg",
    icon: Box,
  },
  {
    title: "Retailers & Distributors",
    desc: "Drive product sales with promotions and rewards.",
    image: "/images/products/retailers-distributors.jpg",
    icon: Store,
  },
  {
    title: "Sales Team",
    desc: "Motivate and reward high performance.",
    image: "/images/products/sales-team.jpg",
    icon: Briefcase,
  },
  {
    title: "End Customers",
    desc: "Build lasting loyalty with personalized offers.",
    image: "/images/products/end-customers.jpg",
    icon: Smile,
  },
];

const EkRewardsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#010A17] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Top Navigation */}
      <StackNavbar />

      <main className="flex-1">
        {/* =========================================================================
            SECTION 1: HERO SECTION (Exact Match to Figma: 1440px x 617px)
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden bg-[#030b18]"
          style={{ minHeight: "617px" }}
        >
          {/* Full-width continuous background graphic: ZERO visible edges */}
          <img
            src="/images/products/ek-rewards-hero.jpg"
            alt="EKRewards Unified Platform Preview"
            className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none select-none z-0"
            style={{ minHeight: "617px" }}
          />

          {/* Main Hero Content (Left aligned, vertically centered) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] min-h-[617px] flex items-center px-6 sm:px-10 lg:px-[66px] xl:px-[72px] pt-[84px] sm:pt-[97px] pb-12 lg:pb-0">
            <div className="max-w-[530px] flex flex-col items-start">
              {/* Breadcrumbs / Eyebrow */}
              <div className="flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8] select-none">
                <Link
                  to="/what-we-do"
                  className="hover:text-white transition-colors duration-200"
                >
                  WHAT WE DO
                </Link>
                <span className="text-[#4E6888]">&gt;</span>
                <Link
                  to="/what-we-do#products"
                  className="hover:text-white transition-colors duration-200"
                >
                  PRODUCTS
                </Link>
                <span className="text-[#4E6888]">&gt;</span>
                <span className="text-[#9DB5D0]">EKREWARDS</span>
              </div>

              {/* Main Headline */}
              <h1 className="mt-4 sm:mt-5 text-[38px] sm:text-[46px] lg:text-[50px] xl:text-[54px] font-extrabold leading-[1.08] tracking-[-0.025em] text-white">
                <span className="block">Give Your</span>
                <span className="block">Customers More</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D2EE] via-[#00BAEB] to-[#0090FF]">
                  Reasons to Return
                </span>
              </h1>

              {/* Subtitle / Paragraph Description */}
              <p className="mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] sm:leading-[26px] text-[#8EA3BD] max-w-[490px]">
                EKRewards helps businesses motivate sales teams, strengthen
                customer loyalty, run promotions and increase product reach
                through a unified digital rewards platform.
              </p>

              {/* CTA Button: Schedule a Demo */}
              <div className="mt-8 sm:mt-9">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14.5px] sm:text-[15px] font-bold text-[#06152B] shadow-[0_0_24px_rgba(0,210,238,0.45)] hover:shadow-[0_0_36px_rgba(0,210,238,0.75)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  <span>Schedule a Demo</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 2: PRODUCT CAPABILITIES (One Platform, Multiple Possibilities)
            Background: rgba(236, 245, 253, 1)
            Card Specs from Figma Inspect:
            Width: Fixed (310px), Height: Hug (235px), Radius: 16px, Border: 1px rgba(226, 232, 240, 1)
            Padding: 20px, Gap: 10px, Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px] xl:px-[80px]">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  PRODUCT CAPABILITIES
                </p>
                <h2 className="mt-3 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.15] tracking-tight text-[#06152B]">
                  One Platform, Multiple Possibilities
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  Engage your customers, empower your sales teams and drive
                  business growth with a flexible rewards and loyalty platform
                  built for modern enterprises.
                </p>
              </div>
            </div>

            {/* 8 Cards Grid (4 per row on desktop, max-w 310px each matching Figma) */}
            <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 justify-items-center">
              {CAPABILITIES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="w-full max-w-[310px] min-h-[220px] rounded-[16px] p-5 sm:p-[22px] flex flex-col justify-between text-left shadow-[0_2px_12px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-200 select-none group"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(226, 232, 240, 1)",
                    }}
                  >
                    <div>
                      {/* Icon Circle Badge */}
                      <div
                        className="w-[46px] h-[46px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                        style={{ backgroundColor: item.iconBg }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.2}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-4 text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 3: HOW IT WORKS (Launch Your Rewards Program in Three Simple Steps)
            Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px] xl:px-[80px]">
            <div className="grid grid-cols-1 lg:grid-cols-[490px_1fr] xl:grid-cols-[520px_1fr] gap-10 lg:gap-12 xl:gap-16 items-center">
              {/* Left Column: Heading and Description */}
              <div className="flex flex-col items-start max-w-[500px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  HOW IT WORKS
                </p>

                <h2 className="mt-3.5 text-[28px] sm:text-[32px] md:text-[36px] lg:text-[38px] xl:text-[40px] font-extrabold leading-[1.18] tracking-tight text-[#06152B]">
                  <span className="block whitespace-nowrap">Launch Your Rewards</span>
                  <span className="block whitespace-nowrap">
                    <span>Program in </span>
                    <span className="text-[#0075FF]">Three Simple</span>
                  </span>
                  <span className="block whitespace-nowrap text-[#00D2EE]">Steps</span>
                </h2>

                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] text-[#51627C]">
                  Go from setup to impact with a simple, guided process designed
                  for speed and flexibility.
                </p>
              </div>

              {/* Right Column: 3 Step Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-5.5">
                {HOW_IT_WORKS_STEPS.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div
                      key={step.number}
                      className="relative overflow-hidden rounded-[18px] p-6 sm:p-6.5 flex flex-col justify-between text-left shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-200 select-none group min-h-[235px]"
                      style={{
                        backgroundColor: "#F9FBFF",
                        border: "1px solid rgba(226, 232, 240, 1)",
                      }}
                    >
                      {/* Top-Right Number Badge (Matching Screenshot: 01, 02, 03) */}
                      <div
                        className="absolute top-0 right-0 rounded-bl-[14px] px-3.5 py-1 text-[13px] font-extrabold text-white shadow-sm"
                        style={{
                          background:
                            "linear-gradient(90deg, #00D2EE 0%, #0075FF 100%)",
                        }}
                      >
                        {step.number}
                      </div>

                      <div>
                        {/* Circular Icon Badge */}
                        <div
                          className="w-[44px] h-[44px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                          style={{ backgroundColor: step.iconBg }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: step.iconColor }}
                            strokeWidth={2.2}
                          />
                        </div>

                        {/* Step Title */}
                        <h3 className="mt-5 text-[17px] sm:text-[17.5px] font-bold text-[#06152B] tracking-tight leading-snug">
                          {step.title}
                        </h3>

                        {/* Step Description */}
                        <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 4: DATA-DRIVEN GROWTH (Analyze. Engage. Grow.)
            Background: rgba(6, 15, 30, 1)
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(6, 15, 30, 1)" }}
        >
          {/* Ambient Lighting Glow behind 3D Dashboards */}
          <div
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[650px] h-[550px] pointer-events-none z-0"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, rgba(0, 130, 255, 0.25) 0%, rgba(0, 210, 238, 0.12) 40%, transparent 72%)",
            }}
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px] xl:px-[80px]">
            <div className="grid grid-cols-1 lg:grid-cols-[480px_1fr] xl:grid-cols-[510px_1fr] gap-12 lg:gap-14 xl:gap-16 items-center">
              {/* Left Column: Heading, Description & 4 Features */}
              <div className="flex flex-col items-start z-10">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8]">
                  DATA-DRIVEN GROWTH
                </p>

                <h2 className="mt-3.5 text-[28px] sm:text-[34px] lg:text-[38px] xl:text-[42px] font-extrabold leading-[1.12] tracking-tight text-white whitespace-nowrap">
                  Analyze. Engage. Grow.
                </h2>

                <p className="mt-4 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#8EA3BD] max-w-[480px]">
                  Get a complete view of your rewards program, customer
                  engagement and sales performance. Make smarter decisions with
                  real-time analytics and actionable insights.
                </p>

                {/* 4 Feature Items */}
                <div className="mt-8 sm:mt-9 flex flex-col gap-5 sm:gap-6 w-full max-w-[480px]">
                  {ANALYTICS_FEATURES.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.title}
                        className="flex items-start gap-4 sm:gap-4.5 select-none"
                      >
                        <div
                          className="w-[44px] h-[44px] sm:w-[46px] sm:h-[46px] rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: item.iconBg }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: item.iconColor }}
                            strokeWidth={2.2}
                          />
                        </div>
                        <div className="flex flex-col">
                          <h3 className="text-[15.5px] sm:text-[16px] font-bold text-white tracking-tight leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-[#8EA3BD] mt-0.5">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Column: 3D Floating Dashboard Cards PNG */}
              <div className="relative w-full flex items-center justify-center lg:justify-end z-10">
                <div className="relative w-full max-w-[620px] lg:max-w-[720px] xl:max-w-[760px]">
                  <img
                    src="/images/products/ek-rewards-analytics.png"
                    alt="Analyze Engage Grow - Comprehensive Analytics Dashboard Preview"
                    className="w-full h-auto object-contain select-none pointer-events-none drop-shadow-[0_25px_60px_rgba(0,0,0,0.65)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 5: BUILT FOR EVERY STAKEHOLDER (Drive Impact Across the Ecosystem.)
            Background: rgba(248, 250, 252, 1)
            Cards Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(248, 250, 252, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px] xl:px-[80px]">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  BUILT FOR EVERY STAKEHOLDER
                </p>
                <h2 className="mt-3 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.15] tracking-tight text-[#06152B]">
                  <span>Drive Impact Across </span>
                  <span className="text-[#0075FF]">the</span>
                  <br />
                  <span className="text-[#00D2EE]">Ecosystem.</span>
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  Whether you work with channel partners, retailers, distributors,
                  sales teams or end customers, EKRewards adapts to your business
                  model and helps you create meaningful engagement.
                </p>
              </div>
            </div>

            {/* 4 Cards Grid */}
            <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {STAKEHOLDERS.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="overflow-hidden rounded-[18px] flex flex-col justify-between text-left shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_32px_rgba(0,0,0,0.08)] hover:-translate-y-1.5 transition-all duration-300 select-none group"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(226, 232, 240, 1)",
                    }}
                  >
                    {/* Top Image with semi-transparent frosted badge on top-left */}
                    <div className="relative w-full h-[175px] sm:h-[180px] overflow-hidden bg-slate-100">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                      {/* Frosted Glass Badge on top-left */}
                      <div className="absolute top-3.5 left-3.5 w-[38px] h-[38px] rounded-full bg-white/25 backdrop-blur-md border border-white/40 shadow-sm flex items-center justify-center text-white">
                        <Icon className="w-4 h-4 text-white" strokeWidth={2.3} />
                      </div>
                    </div>

                    {/* Bottom Content */}
                    <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                          {card.title}
                        </h3>
                        <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                          {card.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 6: CTA BANNER (Ready to keep customers coming back ?)
            Background: Mountain Summit Sunset with User Provided Image
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden flex items-center py-20 sm:py-24 lg:py-28"
          style={{ backgroundColor: "#011021", minHeight: "440px" }}
        >
          {/* Background Mountain Summit Banner provided by user */}
          <img
            src="/images/products/ek-rewards-cta-bg.png"
            alt="Ready to keep customers coming back"
            className="absolute inset-0 h-full w-full object-cover object-right sm:object-center select-none pointer-events-none"
          />

          {/* Soft Left-to-Right gradient overlay to ensure perfect contrast across screen sizes */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(1, 16, 33, 0.95) 0%, rgba(1, 16, 33, 0.82) 35%, rgba(1, 16, 33, 0.2) 65%, transparent 100%)",
            }}
          />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px] xl:px-[80px]">
            <div className="max-w-[540px] flex flex-col items-start">
              {/* Headline */}
              <h2 className="text-[30px] sm:text-[36px] lg:text-[42px] xl:text-[46px] font-extrabold leading-[1.16] tracking-tight text-white max-w-[620px]">
                <span className="block whitespace-nowrap">Ready to keep customers</span>
                <span className="block whitespace-nowrap">coming back ?</span>
              </h2>

              {/* Subtitle / Description */}
              <p className="mt-4 sm:mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] sm:leading-[26px] text-[#A0B3CC] max-w-[460px]">
                Talk to our team to see how EKRewards can help you build loyalty,
                drive sales and grow your business.
              </p>

              {/* Action Button: Talk to Our Team */}
              <div className="mt-8 sm:mt-9">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14.5px] sm:text-[15px] font-bold text-[#06152B] shadow-[0_0_28px_rgba(0,210,238,0.35)] hover:shadow-[0_0_40px_rgba(0,210,238,0.7)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default EkRewardsPage;
